import { IncidentModel } from '@app/models/incident.model';
import { RimsStepSchema, RimsStepProperty } from '@app/models/rims.model';
import { TechnicalStepModel } from '@app/models/technical-step.model';
import {
  getRimsPropertySchemaAtPath,
  getValueAtPath,
  hasPath,
  setValueAtPathIfExists,
} from '@app/utils/rims/rims.utils';
import { IncidentDetailsPageStore } from '../incident-details-page.store';
import {
  IncidentCustomFieldName,
  IncidentRootFieldName,
  MergeRow,
  MergeRowBase,
  RimsStepExpandableRow,
  SingleColumnMergeRow,
} from './models/merge-row.model';
import { ArticleReferenceModel } from '@app/models/article-reference.model';
import { MediaReferenceMetadataModel } from '@app/models/media-reference-metadata.model';
import { MergeColumn, MergeColumnValueType } from './models/merge-column.model';
import { formatRimsStepPropertyValue, getRimsStepItemId } from './merge-view-rows';
import { RimsStepRow } from './models/merge-row.model';
import { FieldReferenceService } from '../field-references.service';
import { findRowDefinitionByField } from './merge-view-row-definitions';

// Fields that are bound to the incident details form group
const FORM_SYNCED_FIELDS = new Set<IncidentRootFieldName>([
  'title',
  'summary',
  'motivation',
  'isReviewed',
  'startedAt',
  'endedAt',
]);

export function setCheckedFieldToMergedIncident(
  row: MergeRowBase,
  store: IncidentDetailsPageStore,
  fieldRefService: FieldReferenceService,
  onFormFieldApplied?: () => void,
  rimsStepSchemas: Record<string, RimsStepSchema> = {},
): boolean {
  const originalIncident = store.mergeViewOriginal();
  const duplicateIncidents = store.mergeViewDuplicates();
  const targetIncident = store.incident();

  if (!originalIncident || duplicateIncidents == null || !targetIncident) {
    return false;
  }

  // Find the column of the incident which is checked.
  let checkedColumn: MergeColumn | undefined;

  if ((row as MergeRow) && 'columns' in row) {
    checkedColumn = (row as MergeRow).columns.find(
      (column) => column.isChecked === true && column.incidentId !== undefined,
    );
  } else if ((row as SingleColumnMergeRow) && 'column' in row) {
    const singleColMergeRow = row as SingleColumnMergeRow;

    if (singleColMergeRow.column.isChecked) {
      checkedColumn = (row as SingleColumnMergeRow).column;
    }
  }

  if (!checkedColumn?.incidentId) {
    // No column is checked anymore, so the field falls back to its unedited value.
    return resetUncheckedFieldOnMergedIncident(row, store, fieldRefService, onFormFieldApplied, rimsStepSchemas);
  }

  // Finds the source of the checked value.
  const sourceIncident = findSourceIncidentById(checkedColumn.incidentId, originalIncident, duplicateIncidents);
  if (!sourceIncident) {
    return false;
  }

  // Field to be merged is a root level in the IncidentModel
  if (isIncidentRootFieldName(row.field)) {
    applyIncidentRootFieldSilently(targetIncident, sourceIncident, row.field);
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(sourceIncident));

    if (row.field === 'technicalSteps') {
      store.isRimsEdited = true;
    }

    if (FORM_SYNCED_FIELDS.has(row.field)) {
      // Callback that syncs the form group in the incident details page
      onFormFieldApplied?.();

      fieldRefService.addIncidentFieldReference(
        row.field.charAt(0).toUpperCase() + row.field.slice(1),
        sourceIncident.id,
      );
    }

    return true;
  }

  // Field needs a custom mapping, it can't be directly transferred from the source to the target IncidentModel
  if (isIncidentCustomFieldName(row.field)) {
    return setCheckedCustomFieldToMergedIncident(
      row,
      store,
      fieldRefService,
      sourceIncident,
      targetIncident,
      onFormFieldApplied,
    );
  }

  // Field is a RIMS step field
  if (isIncidentRimsFieldName(row.field)) {
    return setCheckedRimsFieldToMergedIncident(
      row.field,
      sourceIncident,
      targetIncident,
      store,
      fieldRefService,
      row,
      rimsStepSchemas,
    );
  }

  // Other field families (entity, actor, rims) are intentionally handled later.
  return false;
}

function resetUncheckedFieldOnMergedIncident(
  row: MergeRowBase,
  store: IncidentDetailsPageStore,
  fieldRefService: FieldReferenceService,
  onFormFieldApplied?: () => void,
  rimsStepSchemas: Record<string, RimsStepSchema> = {},
): boolean {
  const uneditedIncident = store.uneditedIncident();
  const targetIncident = store.incident();

  if (!uneditedIncident || !targetIncident) {
    return false;
  }

  if (isIncidentRootFieldName(row.field)) {
    applyIncidentRootFieldSilently(targetIncident, uneditedIncident, row.field);
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(uneditedIncident));

    if (row.field === 'technicalSteps') {
      store.isRimsEdited = true;
    }

    if (FORM_SYNCED_FIELDS.has(row.field)) {
      // Callback that syncs the form group in the incident details page
      onFormFieldApplied?.();

      fieldRefService.uncheckIncidentFieldReference(row.field.charAt(0).toUpperCase() + row.field.slice(1));
    }

    return true;
  }

  if (isIncidentCustomFieldName(row.field)) {
    return resetUncheckedCustomFieldOnMergedIncident(
      row,
      fieldRefService,
      uneditedIncident,
      targetIncident,
      onFormFieldApplied,
    );
  }

  if (isIncidentRimsFieldName(row.field)) {
    return resetUncheckedRimsFieldOnMergedIncident(
      row.field,
      uneditedIncident,
      targetIncident,
      store,
      fieldRefService,
      row,
      rimsStepSchemas,
    );
  }

  return false;
}

// Applies the merged/reset value to whichever column the row exposes, mutating the row's own column object in place.
function applyValueToRowColumn(row: MergeRowBase, value: MergeColumnValueType | null | undefined): void {
  if ('columns' in row) {
    const mergeRow = row as MergeRow;
    if (mergeRow.columns[0]) {
      mergeRow.columns[0].value = value ?? null;
    }
  } else if ('column' in row) {
    (row as SingleColumnMergeRow).column.value = value ?? null;
  }
}

function resetUncheckedCustomFieldOnMergedIncident(
  row: MergeRowBase,
  fieldRefService: FieldReferenceService,
  uneditedIncident: IncidentModel,
  targetIncident: IncidentModel,
  onFormFieldApplied?: () => void,
): boolean {
  if (row.field === 'custom.timeSpanOfAttack') {
    targetIncident.startedAt = uneditedIncident.startedAt;
    targetIncident.endedAt = uneditedIncident.endedAt;
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(uneditedIncident));

    fieldRefService.uncheckIncidentFieldReference('StartedAt');
    fieldRefService.uncheckIncidentFieldReference('EndedAt');

    onFormFieldApplied?.();
    return true;
  }

  if (row.field === 'custom.entity') {
    targetIncident.entityId = uneditedIncident.entityId;
    targetIncident.entity = uneditedIncident.entity;
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(uneditedIncident));

    fieldRefService.uncheckIncidentFieldReference('EntityId');
    return true;
  }

  if (row.field === 'custom.actor') {
    targetIncident.actorId = uneditedIncident.actorId;
    targetIncident.actor = uneditedIncident.actor;
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(uneditedIncident));

    fieldRefService.uncheckIncidentFieldReference('ActorId');
    return true;
  }

  // Unmatched reference rows have no counterpart on the unedited incident, so only the reference is dropped.
  if (row.field === 'custom.articleReference') {
    const singleColMergeRow = row as SingleColumnMergeRow;

    if (singleColMergeRow.column) {
      fieldRefService.removeArticleFieldReference(singleColMergeRow.column.value as ArticleReferenceModel);
    }
  }

  if (row.field === 'custom.mediaReference') {
    const singleColMergeRow = row as SingleColumnMergeRow;

    if (singleColMergeRow.column) {
      fieldRefService.removeMediaFieldReference(singleColMergeRow.column.value as MediaReferenceMetadataModel);
    }
  }

  return false;
}

function resetUncheckedRimsFieldOnMergedIncident(
  field: `rims.${string}`,
  uneditedIncident: IncidentModel,
  targetIncident: IncidentModel,
  store: IncidentDetailsPageStore,
  fieldReferenceService: FieldReferenceService,
  row: MergeRowBase,
  rimsStepSchemas: Record<string, RimsStepSchema>,
): boolean {
  const parsedField = parseRimsFieldPath(field);
  if (!parsedField) {
    return false;
  }

  if (!applyRimsFieldFromSourceIncident(parsedField, uneditedIncident, targetIncident)) {
    return false;
  }

  applyValueToRowColumn(
    row,
    getRimsFieldValueFromIncident(parsedField, uneditedIncident, rimsStepSchemas[parsedField.stepId]),
  );

  fieldReferenceService.uncheckTechnicalStepFieldReference(parsedField.stepId, parsedField.propertyPath);
  store.isRimsEdited = true;
  return true;
}

// Reads the raw value from the incident and formats it for display the same way the initial row is built (e.g. dates), based on the RIMS step schema.
function getRimsFieldValueFromIncident(
  parsedField: { stepId: string; propertyPath: string },
  incident: IncidentModel,
  stepSchema?: RimsStepSchema,
): MergeColumnValueType {
  const technicalStep = incident.technicalSteps?.find((step) => step.rimsStep?.$type === parsedField.stepId);
  if (!technicalStep || !hasPath(technicalStep.rimsStep, parsedField.propertyPath)) {
    return null;
  }

  const rawValue = getValueAtPath(technicalStep.rimsStep, parsedField.propertyPath);
  const propertySchema = getRimsPropertySchemaAtPath(stepSchema?.properties, parsedField.propertyPath);

  if (!propertySchema) {
    // Schema not loaded/resolved yet, fall back to the raw stored value.
    return rawValue as MergeColumnValueType;
  }

  const propertyName = parsedField.propertyPath.slice(parsedField.propertyPath.lastIndexOf('/') + 1);
  return formatRimsStepPropertyValue(propertyName, rawValue as RimsStepProperty, propertySchema);
}

// The reason we apply field value silently is because we don't to trigger the signal dependencies of the IncidentModel. That will refresh the whole merge-view grid.
// We want to handle it manually in order to be more optimized.
function applyIncidentRootFieldSilently<K extends IncidentRootFieldName>(
  targetIncident: IncidentModel,
  sourceIncident: IncidentModel,
  field: K,
): void {
  targetIncident[field] = sourceIncident[field];
}

function isIncidentRootFieldName(field: MergeRow['field']): field is IncidentRootFieldName {
  return !!field && !field.includes('.');
}

function isIncidentRimsFieldName(field: MergeRow['field']): field is `rims.${string}` {
  return field?.startsWith('rims.') ?? false;
}

function isIncidentCustomFieldName(field: MergeRow['field']): field is IncidentCustomFieldName {
  // Custom means that the field will need a custom mapping
  return field?.startsWith('custom.') ?? false;
}

function setCheckedCustomFieldToMergedIncident(
  row: MergeRowBase,
  store: IncidentDetailsPageStore,
  fieldRefService: FieldReferenceService,
  sourceIncident: IncidentModel,
  targetIncident: IncidentModel,
  onFormFieldApplied?: () => void,
): boolean {
  if (row.field === 'custom.timeSpanOfAttack') {
    targetIncident.startedAt = sourceIncident.startedAt;
    targetIncident.endedAt = sourceIncident.endedAt;
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(sourceIncident));

    fieldRefService.addIncidentFieldReference('StartedAt', sourceIncident.id);

    fieldRefService.addIncidentFieldReference('EndedAt', sourceIncident.id);

    onFormFieldApplied?.();
    return true;
  }

  if (row.field === 'custom.entity') {
    targetIncident.entityId = sourceIncident.entityId;
    targetIncident.entity = sourceIncident.entity;
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(sourceIncident));

    fieldRefService.addIncidentFieldReference('EntityId', sourceIncident.id);
    return true;
  }

  if (row.field === 'custom.actor') {
    targetIncident.actorId = sourceIncident.actorId;
    targetIncident.actor = sourceIncident.actor;
    applyValueToRowColumn(row, findRowDefinitionByField(row.field)?.valueSelector(sourceIncident));

    fieldRefService.addIncidentFieldReference('ActorId', sourceIncident.id);
    return true;
  }

  if (row.field === 'custom.articleReference') {
    const singleColMergeRow = row as SingleColumnMergeRow;

    if (singleColMergeRow.column) {
      const articleReference = singleColMergeRow.column.value as ArticleReferenceModel;
      fieldRefService.addArticleFieldReference(articleReference);
    }
  }

  if (row.field === 'custom.mediaReference') {
    const singleColMergeRow = row as SingleColumnMergeRow;

    if (singleColMergeRow.column) {
      const mediaReference = singleColMergeRow.column.value as MediaReferenceMetadataModel;
      fieldRefService.addMediaFieldReference(mediaReference);
    }
  }

  return false;
}

function setCheckedRimsFieldToMergedIncident(
  field: `rims.${string}`,
  sourceIncident: IncidentModel,
  targetIncident: IncidentModel,
  store: IncidentDetailsPageStore,
  fieldRefService: FieldReferenceService,
  row: MergeRowBase,
  rimsStepSchemas: Record<string, RimsStepSchema>,
): boolean {
  const parsedField = parseRimsFieldPath(field);
  if (!parsedField) {
    return false;
  }

  if (!applyRimsFieldFromSourceIncident(parsedField, sourceIncident, targetIncident)) {
    return false;
  }

  applyValueToRowColumn(
    row,
    getRimsFieldValueFromIncident(parsedField, sourceIncident, rimsStepSchemas[parsedField.stepId]),
  );

  const sourceTechnicalStepId = sourceIncident.technicalSteps?.find(
    (step) => step.rimsStep?.$type === parsedField.stepId,
  )?.id;

  if (!sourceTechnicalStepId) {
    return false;
  }

  fieldRefService.addTechnicalStepFieldFieldReference(
    parsedField.stepId,
    parsedField.propertyPath,
    sourceTechnicalStepId,
  );

  store.isRimsEdited = true;
  return true;
}

function applyRimsFieldFromSourceIncident(
  parsedField: { stepId: string; propertyPath: string },
  sourceIncident: IncidentModel,
  targetIncident: IncidentModel,
): boolean {
  const { stepId, propertyPath } = parsedField;
  const sourceTechnicalStep = sourceIncident.technicalSteps?.find((step) => step.rimsStep?.$type === stepId);
  if (!sourceTechnicalStep || !hasPath(sourceTechnicalStep.rimsStep, propertyPath)) {
    // Step does not exist in the source incident.
    return false;
  }

  if (!targetIncident.technicalSteps || targetIncident.technicalSteps.length === 0) {
    return false;
  }

  const targetTechnicalStepIndex = targetIncident.technicalSteps.findIndex((step) => step.rimsStep?.$type === stepId);
  if (targetTechnicalStepIndex < 0) {
    // Step does not exist in the target incident.
    return false;
  }

  const targetTechnicalStep = targetIncident.technicalSteps[targetTechnicalStepIndex];
  if (!hasPath(targetTechnicalStep.rimsStep, propertyPath)) {
    return false;
  }

  const sourceValue = getValueAtPath(sourceTechnicalStep.rimsStep, propertyPath);
  const updatedRimsStep = structuredClone(targetTechnicalStep.rimsStep);
  if (!setValueAtPathIfExists(updatedRimsStep, propertyPath, structuredClone(sourceValue))) {
    return false;
  }

  const updatedTechnicalSteps: TechnicalStepModel[] = [...targetIncident.technicalSteps];
  updatedTechnicalSteps[targetTechnicalStepIndex] = {
    ...targetTechnicalStep,
    rimsStep: updatedRimsStep,
  };

  targetIncident.technicalSteps = updatedTechnicalSteps;
  return true;
}

function parseRimsFieldPath(field: `rims.${string}`): { stepId: string; propertyPath: string } | null {
  const fieldWithoutPrefix = field.slice('rims.'.length);
  const firstSlashIndex = fieldWithoutPrefix.indexOf('/');

  if (firstSlashIndex <= 0 || firstSlashIndex === fieldWithoutPrefix.length - 1) {
    return null;
  }

  const stepId = fieldWithoutPrefix.slice(0, firstSlashIndex);
  const propertyPath = fieldWithoutPrefix.slice(firstSlashIndex + 1);

  if (stepId.length === 0 || propertyPath.length === 0) {
    return null;
  }

  return {
    stepId,
    propertyPath,
  };
}

function findSourceIncidentById(
  incidentId: number,
  originalIncident: IncidentModel,
  duplicateIncidents: IncidentModel[],
): IncidentModel | null {
  if (originalIncident.id === incidentId) {
    return originalIncident;
  }

  return duplicateIncidents.find((duplicateIncident) => duplicateIncident.id === incidentId) ?? null;
}

// Replaces the merged incident's set item sharing the checked item's id, or inserts it if the merged incident does not have it yet.
export function setCheckedRimsSetItemToMergedIncident(
  itemRow: RimsStepExpandableRow,
  store: IncidentDetailsPageStore,
  fieldRefService: FieldReferenceService,
): boolean {
  const originalIncident = store.mergeViewOriginal();
  const duplicateIncidents = store.mergeViewDuplicates();
  const targetIncident = store.incident();

  if (!originalIncident || duplicateIncidents == null || !targetIncident) {
    return false;
  }

  const checkedColumn = (itemRow.columns ?? []).find(
    (column) => column.isChecked === true && column.incidentId !== undefined,
  );

  if (!checkedColumn?.incidentId) {
    // No column is checked anymore, so the set item falls back to its unedited value.
    return resetUncheckedRimsSetItemOnMergedIncident(itemRow, store, fieldRefService);
  }

  const checkedColumnIndex = (itemRow.columns ?? []).indexOf(checkedColumn);

  if (!itemRow.arrayField || itemRow.itemId == null) {
    return false;
  }

  const sourceIncident = findSourceIncidentById(checkedColumn.incidentId, originalIncident, duplicateIncidents);
  if (!sourceIncident) {
    return false;
  }

  const parsedField = parseRimsFieldPath(itemRow.arrayField);
  if (!parsedField) {
    return false;
  }

  const { stepId, propertyPath } = parsedField;

  const sourceTechnicalStep = sourceIncident.technicalSteps?.find((step) => step.rimsStep?.$type === stepId);
  if (!sourceTechnicalStep || !hasPath(sourceTechnicalStep.rimsStep, propertyPath)) {
    return false;
  }

  const sourceArrayValue = getValueAtPath(sourceTechnicalStep.rimsStep, propertyPath);
  if (!Array.isArray(sourceArrayValue)) {
    return false;
  }

  const sourceItem = sourceArrayValue.find((item) => getRimsStepItemId(item) === itemRow.itemId);
  if (!sourceItem) {
    return false;
  }

  if (!targetIncident.technicalSteps || targetIncident.technicalSteps.length === 0) {
    return false;
  }

  const targetTechnicalStepIndex = targetIncident.technicalSteps.findIndex((step) => step.rimsStep?.$type === stepId);
  if (targetTechnicalStepIndex < 0) {
    return false;
  }

  const targetTechnicalStep = targetIncident.technicalSteps[targetTechnicalStepIndex];
  if (!hasPath(targetTechnicalStep.rimsStep, propertyPath)) {
    return false;
  }

  const targetArrayValue = getValueAtPath(targetTechnicalStep.rimsStep, propertyPath);
  const updatedArray = Array.isArray(targetArrayValue) ? structuredClone(targetArrayValue) : [];
  const clonedSourceItem = structuredClone(sourceItem);

  const existingItemIndex = updatedArray.findIndex((item) => getRimsStepItemId(item) === itemRow.itemId);
  if (existingItemIndex >= 0) {
    updatedArray[existingItemIndex] = clonedSourceItem;
  } else {
    updatedArray.push(clonedSourceItem);
  }

  const updatedRimsStep = structuredClone(targetTechnicalStep.rimsStep);
  if (!setValueAtPathIfExists(updatedRimsStep, propertyPath, updatedArray)) {
    return false;
  }

  const updatedTechnicalSteps: TechnicalStepModel[] = [...targetIncident.technicalSteps];
  updatedTechnicalSteps[targetTechnicalStepIndex] = {
    ...targetTechnicalStep,
    rimsStep: updatedRimsStep,
  };

  targetIncident.technicalSteps = updatedTechnicalSteps;

  fieldRefService.addTechnicalStepSetItemFieldReference(
    parsedField.stepId,
    getRimsStepItemId(sourceItem)!,
    sourceTechnicalStep.id,
  );

  // The whole set item is sourced from this column, so every field nested inside it is checked too.
  setRimsSetItemChildrenCheckedState(itemRow.children, checkedColumnIndex);

  store.isRimsEdited = true;
  return true;
}

// Reverts the merged incident's set item back to its unedited value, or drops it if it didn't exist before editing.
function resetUncheckedRimsSetItemOnMergedIncident(
  itemRow: RimsStepExpandableRow,
  store: IncidentDetailsPageStore,
  fieldRefService: FieldReferenceService,
): boolean {
  const uneditedIncident = store.uneditedIncident();
  const targetIncident = store.incident();

  if (!uneditedIncident || !targetIncident || !itemRow.arrayField || itemRow.itemId == null) {
    return false;
  }

  const parsedField = parseRimsFieldPath(itemRow.arrayField);
  if (!parsedField) {
    return false;
  }

  const { stepId, propertyPath } = parsedField;

  if (!targetIncident.technicalSteps || targetIncident.technicalSteps.length === 0) {
    return false;
  }

  const targetTechnicalStepIndex = targetIncident.technicalSteps.findIndex((step) => step.rimsStep?.$type === stepId);
  if (targetTechnicalStepIndex < 0) {
    return false;
  }

  const targetTechnicalStep = targetIncident.technicalSteps[targetTechnicalStepIndex];
  if (!hasPath(targetTechnicalStep.rimsStep, propertyPath)) {
    return false;
  }

  const targetArrayValue = getValueAtPath(targetTechnicalStep.rimsStep, propertyPath);
  const updatedArray = Array.isArray(targetArrayValue) ? structuredClone(targetArrayValue) : [];
  const existingItemIndex = updatedArray.findIndex((item) => getRimsStepItemId(item) === itemRow.itemId);

  const uneditedTechnicalStep = uneditedIncident.technicalSteps?.find((step) => step.rimsStep?.$type === stepId);
  const uneditedArrayValue =
    uneditedTechnicalStep && hasPath(uneditedTechnicalStep.rimsStep, propertyPath)
      ? getValueAtPath(uneditedTechnicalStep.rimsStep, propertyPath)
      : null;
  const uneditedItem = Array.isArray(uneditedArrayValue)
    ? uneditedArrayValue.find((item) => getRimsStepItemId(item) === itemRow.itemId)
    : undefined;

  if (uneditedItem) {
    // The set item existed before editing; restore its original values.
    if (existingItemIndex >= 0) {
      updatedArray[existingItemIndex] = structuredClone(uneditedItem);
    } else {
      updatedArray.push(structuredClone(uneditedItem));
    }
  } else if (existingItemIndex >= 0) {
    // The set item was merged in from another incident and has no unedited counterpart, so it's dropped.
    updatedArray.splice(existingItemIndex, 1);
  } else {
    return false;
  }

  const updatedRimsStep = structuredClone(targetTechnicalStep.rimsStep);
  if (!setValueAtPathIfExists(updatedRimsStep, propertyPath, updatedArray)) {
    return false;
  }

  const updatedTechnicalSteps: TechnicalStepModel[] = [...targetIncident.technicalSteps];
  updatedTechnicalSteps[targetTechnicalStepIndex] = {
    ...targetTechnicalStep,
    rimsStep: updatedRimsStep,
  };

  targetIncident.technicalSteps = updatedTechnicalSteps;

  fieldRefService.uncheckTechnicalStepSetItemFieldReference(stepId, itemRow.itemId);

  // No column is checked anymore, so every field nested inside the set item is unchecked too.
  setRimsSetItemChildrenCheckedState(itemRow.children, null);

  store.isRimsEdited = true;
  return true;
}

// Marks every nested field row inside a set item as checked for the given column (or unchecked when columnIndex is null).
function setRimsSetItemChildrenCheckedState(rows: RimsStepRow[], columnIndex: number | null): void {
  for (const row of rows) {
    if (row.kind === 'field') {
      row.row.columns.forEach((column, index) => {
        column.isChecked = columnIndex !== null && index === columnIndex;
      });
      continue;
    }

    setRimsSetItemChildrenCheckedState(row.children, columnIndex);
  }
}
